import React, { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { CSG } from 'three-csg-ts'

const TechnicalCube: React.FC = () => {
  const cubeSize = 1
  const cubeGeometry = useMemo(
    () => new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize),
    [cubeSize]
  )

  const edgesGeometry = useMemo(() => {
    const edges = new THREE.EdgesGeometry(cubeGeometry)
    const posAttr = edges.getAttribute('position')
    const positions = posAttr.array as number[]
    const lineDistances: number[] = []

    for (let i = 0; i < positions.length; i += 6) {
      const x1 = positions[i]
      const y1 = positions[i + 1]
      const z1 = positions[i + 2]
      const x2 = positions[i + 3]
      const y2 = positions[i + 4]
      const z2 = positions[i + 5]
      const segmentLength = Math.sqrt(
        (x2 - x1) ** 2 + (y2 - y1) ** 2 + (z2 - z1) ** 2
      )
      lineDistances.push(0, segmentLength)
    }

    edges.setAttribute(
      'lineDistance',
      new THREE.Float32BufferAttribute(lineDistances, 1)
    )
    return edges
  }, [cubeGeometry])

  return (
    <group rotation={[0, Math.PI / 4 - 0.7, 0]}>
      <mesh geometry={cubeGeometry} renderOrder={0}>
        <meshBasicMaterial
          attach="material"
          color="#02a9f7"
          polygonOffset
          polygonOffsetFactor={1}
          polygonOffsetUnits={1}
        />
      </mesh>
      <lineSegments geometry={edgesGeometry} renderOrder={1}>
        <lineDashedMaterial
          attach="material"
          color="#ffffff"
          dashSize={0.1}
          gapSize={0.05}
          linewidth={1}
          depthTest={true}
          depthFunc={THREE.GreaterDepth}
        />
      </lineSegments>
      <lineSegments geometry={edgesGeometry} renderOrder={2}>
        <lineBasicMaterial attach="material" color="#ffffff" linewidth={1} />
      </lineSegments>
    </group>
  )
}

const CubeDonut: React.FC = () => {
  const cubeSize = 1

  const mergedMesh = useMemo(() => {
    const positions = [
      [-cubeSize,  cubeSize, 0],
      [ 0,         cubeSize, 0],
      [ cubeSize,  cubeSize, 0],
      [-cubeSize,  0,        0],
      [ cubeSize,  0,        0],
      [-cubeSize, -cubeSize, 0],
      [ 0,        -cubeSize, 0],
      [ cubeSize, -cubeSize, 0],
    ]
    
    const material = new THREE.MeshBasicMaterial({ color: 'white' })
    let unionMesh: THREE.Mesh | null = null

    positions.forEach((pos) => {
      const geometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize)
      geometry.translate(pos[0], pos[1], pos[2])
      const cubeMesh = new THREE.Mesh(geometry, material)

      if (!unionMesh) {
        unionMesh = cubeMesh
      } else {
        const bspA = CSG.fromMesh(unionMesh)
        const bspB = CSG.fromMesh(cubeMesh)
        const bspResult = bspA.union(bspB)
        unionMesh = CSG.toMesh(bspResult, unionMesh.matrix, material)
        unionMesh.geometry.computeVertexNormals()
      }
    })

    if (!unionMesh) {
      throw new Error('No geometry created')
    }
    return unionMesh
  }, [cubeSize])

  const edgesGeometry = useMemo(() => {
    const edges = new THREE.EdgesGeometry(mergedMesh.geometry)
    const posAttr = edges.getAttribute('position')
    const positions = posAttr.array as number[]
    const lineDistances: number[] = []

    for (let i = 0; i < positions.length; i += 6) {
      const x1 = positions[i]
      const y1 = positions[i + 1]
      const z1 = positions[i + 2]
      const x2 = positions[i + 3]
      const y2 = positions[i + 4]
      const z2 = positions[i + 5]
      const segmentLength = Math.sqrt(
        (x2 - x1) ** 2 +
        (y2 - y1) ** 2 +
        (z2 - z1) ** 2
      )
      lineDistances.push(0, segmentLength)
    }

    edges.setAttribute(
      'lineDistance',
      new THREE.Float32BufferAttribute(lineDistances, 1)
    )

    return edges
  }, [mergedMesh.geometry])

  return (
    <group rotation={[-Math.PI / 2, 0, Math.PI / 4 - 0.7]}>
      <mesh geometry={mergedMesh.geometry} renderOrder={0}>
        <meshBasicMaterial 
          color="#02577A"
          polygonOffset 
          polygonOffsetFactor={1} 
          polygonOffsetUnits={1} 
        />
      </mesh>
      <lineSegments geometry={edgesGeometry} renderOrder={1}>
        <lineDashedMaterial
          color="#ffffff"
          dashSize={0.1}
          gapSize={0.05}
          linewidth={1}
          depthTest={true}
          depthFunc={THREE.GreaterDepth}
        />
      </lineSegments>
      <lineSegments geometry={edgesGeometry} renderOrder={2}>
        <lineBasicMaterial color="#ffffff" linewidth={1} />
      </lineSegments>
    </group>
  )
}

type CubeState =
  | 'floating'
  | 'pausingAtCenter'
  | 'descending'
  | 'pausingBottom'
  | 'ascending'
  | 'pausingTop'

interface FloatingCubeProps {
  initialX: number
  floatHeight: number
  resetX: number
  floorY: number
  tolerance?: number
  velocity?: number
  globalTransit: React.MutableRefObject<{ inTransit: boolean }>
  startX: number
  spacing: number
  numFloating: number
}

const FloatingCube: React.FC<FloatingCubeProps> = ({
  initialX,
  floatHeight,
  resetX,
  floorY,
  tolerance = 0.05,
  velocity = 0.1,
  globalTransit,
  startX,
  spacing,
  numFloating,
}) => {
  const cubeRef = useRef<THREE.Group>(null)

  const stateRef = useRef<{
    state: CubeState
    targetY: number
    velocity: number
    hasTransited: boolean
    pauseTimer: number
  }>({
    state: 'floating',
    targetY: floatHeight,
    velocity,
    hasTransited: false,
    pauseTimer: 0,
  })

  useFrame((_, delta) => {
    if (!cubeRef.current) return
    const cube = cubeRef.current
    const cubeState = stateRef.current

    if (cubeState.state === 'floating') {
      if (!globalTransit.current.inTransit) {
        cube.position.x += cubeState.velocity
      }
      const isOverCenter =
        Math.abs(cube.position.x) < tolerance && Math.abs(cube.position.z) < tolerance
      if (isOverCenter && !cubeState.hasTransited && !globalTransit.current.inTransit) {
        cube.position.x = 0
        cubeState.state = 'pausingAtCenter'
        cubeState.pauseTimer = 0
        cubeState.hasTransited = true
        globalTransit.current.inTransit = true
      }
    } else if (cubeState.state === 'pausingAtCenter') {
      cubeState.pauseTimer += delta
      if (cubeState.pauseTimer >= 1.0) {
        cubeState.state = 'descending'
        cubeState.targetY = floorY
        cubeState.pauseTimer = 0
      }
    } else if (cubeState.state === 'descending') {
      if (cube.position.y > cubeState.targetY) {
        cube.position.y -= 0.02
        if (cube.position.y < cubeState.targetY) cube.position.y = cubeState.targetY
      } else {
        cubeState.state = 'pausingBottom'
        cubeState.pauseTimer = 0
      }
    } else if (cubeState.state === 'pausingBottom') {
      cubeState.pauseTimer += delta
      if (cubeState.pauseTimer >= 1.0) {
        cubeState.state = 'ascending'
        cubeState.targetY = floatHeight
        cubeState.pauseTimer = 0
      }
    } else if (cubeState.state === 'ascending') {
      if (cube.position.y < cubeState.targetY) {
        cube.position.y += 0.02
        if (cube.position.y > cubeState.targetY) cube.position.y = cubeState.targetY
      } else {
        cubeState.state = 'pausingTop'
        cubeState.pauseTimer = 0
      }
    } else if (cubeState.state === 'pausingTop') {
      cubeState.pauseTimer += delta
      if (cubeState.pauseTimer >= 1.0) {
        cube.position.x = 0.1
        cubeState.state = 'floating'
        globalTransit.current.inTransit = false
        cubeState.hasTransited = false
      }
    }

    if (cube.position.x > resetX) {
      cube.position.x = startX - spacing * (numFloating - 1)
      cube.position.y = floatHeight
      cubeState.state = 'floating'
      cubeState.hasTransited = false
      cubeState.pauseTimer = 0
      globalTransit.current.inTransit = false
    }
  })

  return (
    <group ref={cubeRef} position={[initialX, floatHeight, 0]}>
      <TechnicalCube />
    </group>
  )
}

const DataCollectionScene: React.FC = () => {
  const globalTransit = useRef<{ inTransit: boolean }>({ inTransit: false })
  const numFloating = 5
  const floatHeight = 2
  const spacing = 2
  const startX = -5
  const resetX = 5

  const floatingCubes = useMemo(() => {
    const cubes: JSX.Element[] = []
    for (let i = 0; i < numFloating; i++) {
      cubes.push(
        <FloatingCube
          key={`floating-${i}`}
          initialX={startX - i * spacing}
          floatHeight={floatHeight}
          resetX={resetX}
          floorY={0}
          globalTransit={globalTransit}
          startX={startX}
          spacing={spacing}
          numFloating={numFloating}
        />
      )
    }
    return cubes
  }, [numFloating, floatHeight, spacing, startX, resetX]);

  return (
      <group rotation={[0, Math.PI / 4 - 0.7, 0]}>
        <ambientLight intensity={0.5} />
        <hemisphereLight skyColor="#ffffff" groundColor="#444444" intensity={0.8} />
        <directionalLight color="#ffffff" intensity={1.5} position={[10, 20, 10]} castShadow />
        <CubeDonut />
        <group>{floatingCubes}</group>
      </group>
  )
}

export default DataCollectionScene